// How a hub's API key is sent, and a grant credential's recipe: the
// schemas of the credential protocol the Curator API's
// PUT /api/v1/hubs/:hub/secrets/:host takes, and the store and the
// EgressGateway read. Part of the Curator API contract, so it imports only
// effect. See Design > Exchange, "Curator API", and Names and definitions,
// "Grant credential".

import { Schema } from "effect";

// The headers that carry a request itself, which a key may not take the place of.
const RESERVED_HEADERS: ReadonlySet<string> = new Set(["host", "content-length", "transfer-encoding", "connection", "keep-alive", "upgrade", "te", "trailer", "proxy-authorization", "proxy-connection"]);
// The header an API key, or a secret of a grant credential, is sent in: a
// header name, never one that carries the request.
export const ApiKeyHeader = Schema.String.check(Schema.isPattern(/^[A-Za-z0-9-]{1,64}$/), Schema.makeFilter((h) => !RESERVED_HEADERS.has(h.toLowerCase()) || `${h} carries the request itself, so a key can't go in it`));
// The query parameter an API key is sent in, such as api_key: URL-safe
// characters only, compared with case, as a URL's query is.
export const ApiKeyParam = Schema.String.check(Schema.isPattern(/^[A-Za-z0-9_.-]{1,64}$/));
// Which of a host's accounts a key is for, such as one member's account of a
// family hub; "default" unless a tool names one. The Tool SDK's
// ACCOUNT_NAME is the same pattern. See Design > Names and definitions,
// "Account".
export const ApiKeyAccount = Schema.String.check(Schema.isPattern(/^[a-z0-9][a-z0-9_-]{0,63}$/));
// Where a key goes in a request to its host: a header, or a query parameter.
export const ApiKeyPlacement = Schema.Literals(["header", "query"]);
export type ApiKeyPlacement = typeof ApiKeyPlacement.Type;
// What goes before it in the header, such as "Bearer ": printable ASCII
// with no line break, so it can't add another header.
export const ApiKeyPrefix = Schema.String.check(Schema.isMaxLength(64), Schema.isPattern(/^[\x20-\x7e]*$/));

// Where a grant credential's token call goes: an HTTPS URL on the default
// port, with no credentials, query or fragment. Its host may be another than
// the key's, and the EgressGateway checks it against the toolset's egress
// allowlist each time it calls it.
const GrantUrl = Schema.String.check(Schema.isMaxLength(2048), Schema.isPattern(/^https:\/\/[\x21-\x7e]+$/), Schema.makeFilter((u) => {
  if (!URL.canParse(u)) return "isn't a URL";
  const url = new URL(u);
  return url.protocol === "https:" && url.port === "" && url.username === "" && url.password === "" && url.search === "" && url.hash === ""
    || "must be HTTPS on the default port, with no credentials, query or fragment";
}));
// A form parameter a grant adds, such as its scope.
const GrantParam = Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(512), Schema.isPattern(/^[\x20-\x7e]+$/));
// A top-level field of a JSON token response.
const ResponseField = Schema.String.check(Schema.isPattern(/^[A-Za-z0-9_-]{1,64}$/));
// A token call's response: a JWT whose exp gives its expiry, or JSON with
// the token in `token` (access_token), its lifetime in seconds in
// `expiresIn` (expires_in), and a refresh token that replaces the stored one
// in `refreshToken` (refresh_token); `ttlSeconds` is its lifetime when the
// response gives none and the token isn't a JWT with an exp.
const JwtResponse = Schema.Struct({ format: Schema.Literal("jwt") });
const JsonResponse = Schema.Struct({
  format: Schema.Literal("json"),
  token: Schema.optionalKey(ResponseField),
  expiresIn: Schema.optionalKey(ResponseField),
  refreshToken: Schema.optionalKey(ResponseField),
  ttlSeconds: Schema.optionalKey(Schema.Int.check(Schema.isGreaterThan(0), Schema.isLessThanOrEqualTo(7 * 86_400))),
});
// A grant credential's recipe, never a secret: how the EgressGateway gets an
// API key token. key_exchange POSTs the key in `keyHeader` after
// `keyPrefix`, with no body; client_credentials and refresh_token POST an
// OAuth 2.0 form, with the client's ID and secret in Basic auth or the body.
// See Design > Names and definitions, "Grant credential".
export const KeyExchangeGrant = Schema.Struct({
  type: Schema.Literal("key_exchange"), url: GrantUrl, keyHeader: ApiKeyHeader, keyPrefix: Schema.optionalKey(ApiKeyPrefix),
  response: Schema.Union([JwtResponse, JsonResponse]),
});
export const ClientCredentialsGrant = Schema.Struct({
  type: Schema.Literal("client_credentials"), url: GrantUrl, clientAuth: Schema.Literals(["basic", "body"]),
  scope: Schema.optionalKey(GrantParam), audience: Schema.optionalKey(GrantParam), response: JsonResponse,
});
export const RefreshTokenGrant = Schema.Struct({
  type: Schema.Literal("refresh_token"), url: GrantUrl, clientAuth: Schema.Literals(["basic", "body", "none"]),
  scope: Schema.optionalKey(GrantParam), response: JsonResponse,
});
// A jwt_bearer grant: a JWT the EgressGateway signs with the grant's
// private key, RS256 or ES256, with `keyId` as its kid header, `claims`, an
// iat of now, an exp `lifetimeSeconds` later and a random jti, which becomes
// the API key token one of three ways (`exchange`): traded at an OAuth 2.0
// token URL as RFC 7523 says, such as a Google service account's; traded
// for a GitHub App installation token; or sent as it is, such as App Store
// Connect's. See Design > Names and definitions, "Grant credential".
const JwtClaim = Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(512), Schema.isPattern(/^[\x20-\x7e]+$/));
const JwtKeyId = Schema.String.check(Schema.isPattern(/^[\x21-\x7e]{1,128}$/));
const JwtAlg = Schema.Literals(["RS256", "ES256"]);
const lifetime = (min: number, max: number) => Schema.Int.check(Schema.isGreaterThanOrEqualTo(min), Schema.isLessThanOrEqualTo(max));
export const JwtBearerGrant = Schema.Union([
  // claims.aud is the token URL, as RFC 7523 asks.
  Schema.Struct({
    type: Schema.Literal("jwt_bearer"), exchange: Schema.Literal("oauth"), url: GrantUrl, alg: JwtAlg, keyId: Schema.optionalKey(JwtKeyId),
    claims: Schema.Struct({ iss: JwtClaim, sub: Schema.optionalKey(JwtClaim), aud: JwtClaim, scope: Schema.optionalKey(JwtClaim) }),
    lifetimeSeconds: lifetime(60, 3600), response: JsonResponse,
  }),
  // GitHub takes the app's ID as iss and a JWT of at most 10 minutes, and
  // answers { token, expires_at }. url is the installation's
  // access_tokens URL. Its iat is a minute back and its exp lifetimeSeconds
  // after iat, so 120 to 600 leaves it 1 to 9 minutes from now: never
  // expired, and never at GitHub's limit, which a clock a second ahead
  // would pass.
  Schema.Struct({
    type: Schema.Literal("jwt_bearer"), exchange: Schema.Literal("github_installation"), url: GrantUrl, alg: Schema.Literal("RS256"),
    claims: Schema.Struct({ iss: JwtClaim }), lifetimeSeconds: lifetime(120, 600),
  }),
  Schema.Struct({
    type: Schema.Literal("jwt_bearer"), exchange: Schema.Literal("none"), alg: JwtAlg, keyId: Schema.optionalKey(JwtKeyId),
    claims: Schema.Struct({ iss: JwtClaim, sub: Schema.optionalKey(JwtClaim), aud: Schema.optionalKey(JwtClaim), scope: Schema.optionalKey(JwtClaim) }),
    lifetimeSeconds: lifetime(60, 3600),
  }),
]);
export type JwtBearerGrant = typeof JwtBearerGrant.Type;
export const ApiKeyGrant = Schema.Union([KeyExchangeGrant, ClientCredentialsGrant, RefreshTokenGrant, JwtBearerGrant]);
export type ApiKeyGrant = typeof ApiKeyGrant.Type;
